import { Logo } from "@/components/Logo";
import { Avatar } from "@/components/PhotoUpload";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, CalendarDays, UserCheck, BookOpen, Receipt,
  FileText, MessageCircle, Settings as SettingsIcon, Info, LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/siswa", label: "Data Siswa", icon: Users },
  { to: "/jadwal", label: "Jadwal & Pengganti", icon: CalendarDays },
  { to: "/absensi", label: "Absensi", icon: UserCheck },
  { to: "/jurnal", label: "Jurnal Belajar", icon: BookOpen },
  { to: "/pembayaran", label: "Pembayaran", icon: Receipt },
  { to: "/dokumen", label: "Dokumen & Cetak", icon: FileText },
  { to: "/whatsapp", label: "Notifikasi WA", icon: MessageCircle },
  { to: "/pengaturan", label: "Pengaturan", icon: SettingsIcon },
  { to: "/tentang", label: "Tentang Gemma", icon: Info },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-amber-100">
        <Logo size="sm" />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto gemma-scroll">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-200"
                  : "text-slate-600 hover:bg-orange-50 hover:text-orange-600"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-amber-100">
        <NavLink to="/profil" data-testid="profile-link" onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2 mb-2 rounded-xl transition-colors ${isActive ? "bg-orange-100" : "hover:bg-orange-50"}`}>
          <Avatar src={user?.photo} name={user?.name || "?"} size="w-10 h-10" rounded="rounded-full" text="text-base" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
            <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          </div>
        </NavLink>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" /> Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#FAF8F5]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 bg-[#FFF9F2] border-r border-amber-100 flex-col fixed h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />
          <aside className="fixed left-0 top-0 h-screen w-72 bg-[#FFF9F2] z-50 lg:hidden shadow-2xl">
            <SidebarContent />
          </aside>
        </>
      )}

      <div className="flex-1 lg:ml-72 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-amber-100 px-4 h-14 flex items-center gap-3">
          <button data-testid="menu-toggle" onClick={() => setOpen(!open)} className="p-2 -ml-2 text-slate-700">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <Logo size="sm" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">{children}</main>
      </div>
    </div>
  );
}
